<template>
  <div class="inspection-point-view">
    <!-- Mode Not Enabled Message -->
    <v-overlay
      v-if="modeNotEnabled"
      :model-value="true"
      class="align-center justify-center"
      persistent
      scrim="black"
    >
      <v-card class="pa-6 text-center" max-width="500">
        <v-icon size="64" color="warning" class="mb-4">mdi-lock</v-icon>
        <v-card-title class="text-h5 mb-2">{{ $t('Zugriff verweigert') }}</v-card-title>
        <v-card-text class="text-body-1">
          <span v-if="route.name === 'Test' && store.state.session?.testFinished">
            {{ $t('Die Prüfung wurde bereits abgeschlossen.') }}
          </span>
          <span v-else>
            {{ $t(route.name) }} {{ $t('ist in dieser Session nicht freigeschaltet.') }}
          </span>
        </v-card-text>
        <v-card-actions class="justify-center mt-4">
          <v-btn color="primary" @click="router.push('/selection')">{{ $t('Zurück zu Meine Kurse') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-overlay>

    <!-- Auto-Save Indikator für Test-Modus -->
    <div v-if="route.name === 'Test' && isSaving" class="autosave-indicator">
      <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2"></v-progress-circular>
      <span>{{ $t('Speichere...') }}</span>
    </div>
    <div v-if="route.name === 'Test' && !isSaving && lastSaved" class="autosave-indicator saved">
      <v-icon size="16" color="success" class="mr-1">mdi-check-circle</v-icon>
      <span>{{ $t('Gespeichert') }}</span>
    </div>

    <div v-if="showFinishedTestBannerForTrainee" class="readonly-finished-banner">
      <v-icon icon="mdi-alert-circle" size="28" class="mr-3"></v-icon>
      <div>
        <strong>{{ $t('Prüfung abgeschlossen') }}</strong>
        <span> - {{ $t('Diese Prüfung ist abgeschlossen und kann nicht mehr bearbeitet werden.') }}</span>
      </div>
    </div>

    <!-- Status-Banner für Auswertung -->
    <div v-if="route.name === 'EvaluationDetail'" class="evaluation-status-banner" :class="{ 'finished': isTestFinished }">
      <v-icon :icon="isTestFinished ? 'mdi-check-decagram' : 'mdi-clock-outline'" class="mr-2"></v-icon>
      <span v-if="isTestFinished"><strong>{{ $t('Prüfung abgeschlossen') }}</strong>: {{ $t('Die Daten können nicht mehr bearbeitet werden.') }}</span>
      <span v-else><strong>{{ $t('Prüfung noch offen') }}</strong>: {{ $t('Der Azubi hat die Prüfung noch nicht finalisiert.') }}</span>
      
      <v-spacer></v-spacer>

      <!-- Hinweis wenn noch nicht bearbeitet -->
      <span v-if="!isPointProcessed" class="not-processed-hint ml-4">
        <v-icon icon="mdi-alert-circle-outline" size="small" class="mr-1"></v-icon>
        {{ $t('Azubi hat diese Prüfstelle noch nicht bearbeitet.') }}
      </span>
      
      <v-spacer v-if="!isPointProcessed"></v-spacer>
      
      <!-- Wieder freigeben Button für Prüfer -->
      <v-btn 
        v-if="isTestFinished" 
        color="warning" 
        size="small" 
        variant="elevated" 
        @click="reenableTestDialog = true"
        class="ml-4"
      >
        <v-icon start icon="mdi-lock-open-variant"></v-icon>
        {{ $t('Wieder freigeben') }}
      </v-btn>
    </div>

    <!-- Wieder freigeben Overlay -->
    <v-dialog v-model="reenableTestDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">{{ $t('Prüfung wieder freigeben') }}</v-card-title>
        <v-card-text>
          {{ $t('Bist du sicher?') }}<br>
          {{ $t('Dadurch kann der Azubi seine Antworten wieder bearbeiten.') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="reenableTestDialog = false">{{ $t('Abbrechen') }}</v-btn>
          <v-btn color="warning" variant="elevated" @click="confirmReenableTest" :loading="isReenabling">{{ $t('Ja, freigeben') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test abschließen Overlay -->
    <v-dialog v-model="finishTestDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">{{ $t('Prüfung abschließen') }}</v-card-title>
        <v-card-text>
          {{ $t('Bist du sicher?') }}<br>
          {{ $t('Nach dem Abschluss können keine Änderungen mehr vorgenommen werden.') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="finishTestDialog = false">{{ $t('Abbrechen') }}</v-btn>
          <v-btn color="primary" variant="elevated" @click="confirmFinishTest" :loading="isFinishing">{{ $t('Ja, abschließen') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="testFinishedSnackbar" color="success" timeout="5000">
      {{ $t('Prüfung wurde erfolgreich abgeschlossen.') }}
    </v-snackbar>

    <!-- Session Name Info -->
    <div v-if="activeSessionName" class="session-info-banner">
      <v-icon start icon="mdi-book-open-variant" class="mr-2"></v-icon>
      <strong>{{ $t('Sitzung') }}:</strong> {{ activeSessionName }}
    </div>

    <!-- Dropdown-Bereich -->
    <div class="dropdowns">

      <!-- Workpiece Dropdown -->
      <select ref="workpieceSelect" class="dropdown" v-model="selectedWorkpieceId" @change="handleWorkpieceDropdownChange">
        <option value="" disabled selected>{{ $t("Please Choose") }}</option>
        <option v-for="item in workpieces" :key="item._id" :value="item._id">{{ item.title }}</option>
      </select>

      <div class="inspection-selection">
        <!-- Suche nach Prüfstelle -->
        <div v-if="route.name !== 'Test' && route.name !== 'EvaluationDetail'" class="search-field">
          <input
              type="text"
              v-model="searchQuery"
              :placeholder="$t('Search Inspection Point')"
              class="text-input pr-8"
              @focus="showSearchResults = true"
              @blur="handleBlur"
              @input="showSearchResults = true"
          />
          <!-- Löschen-Button (X) -->
          <button 
            v-if="searchQuery" 
            class="clear-search-button" 
            @mousedown.prevent="clearSearch"
            type="button"
          >
            &times;
          </button>
          <!-- Suchergebnis-Dropdown-Liste -->
          <ul v-if="showSearchResults && filteredSearchResults.length > 0" class="search-results-list">
            <li
                v-for="item in filteredSearchResults"
                :key="item._id"
                @mousedown.prevent="selectFromSearchResults(item)"
            >
              {{ item.number }} - {{ item.title }}
            </li>
          </ul>
        </div>
        <!-- Linker Pfeil-Button -->
        <button class="arrow-button arrow-button-left" @click="selectPreviousInspectionPoint">&lt;</button>
        <!-- InspectionPoints Dropdown -->
        <select class="inspection-select" @change="handleInspectionPointDropdownChange" ref="inspectionPointSelect">
          <option v-for="item in filteredInspectionPoints" :key="item._id" :value="item._id">{{ item.title }}</option>
        </select>

        <!-- Rechter Pfeil-Button -->
        <button class="arrow-button" @click="selectNextInspectionPoint">&gt;</button>
      </div>
    </div>

    <!-- Hauptbereich -->
    <div class="main-area">
      <!-- Linker Bereich -->
      <div class="area left">
        <table v-if="selectedWorkpiece && Object.keys(selectedWorkpiece).length > 0" class="info-table">
          <tr class="highlighted-row">
            <td>{{ $t("Name of workpiece") }}</td>
            <td>{{ selectedWorkpiece ? selectedWorkpiece.title : "-" }}</td>
          </tr>
          <tr class="highlighted-row">
            <td>{{ $t("Id of workpiece") }}</td>
            <td>{{ selectedWorkpiece ? selectedWorkpiece.number : "-" }}</td>
          </tr>
          <tr v-if="selectedWorkpiece && selectedWorkpiece.drawing" class="highlighted-row">
            <td>{{ $t("Technical Drawing") }}</td>
            <td>
              <v-btn
                color="primary"
                size="small"
                variant="elevated"
                @click="openDrawing"
              >
                <v-icon start icon="mdi-file-pdf-box"></v-icon>
                {{ $t("Open PDF") }}
              </v-btn>
            </td>
          </tr>
          <tr v-if="selectedWorkpiece && selectedWorkpiece.drawing" class="highlighted-row">
            <td>{{ $t("ISO-GPS") }}</td>
            <td>
              <v-btn
                color="primary"
                size="small"
                variant="elevated"
                @click="openIsoGpsDrawing"
              >
                <v-icon start icon="mdi-file-pdf-box"></v-icon>
                {{ $t("Open PDF") }}
              </v-btn>
            </td>
          </tr>
          <tr class="highlighted-row">
            <td>{{ $t("Inspection Point") }}</td>
            <td>{{ selectedInspectionPoint ? selectedInspectionPoint.title : "-" }}</td>
          </tr>
          <tr class="highlighted-row">
            <td>{{ $t("Id of inspection Point") }}</td>
            <td>{{ selectedInspectionPoint ? selectedInspectionPoint.number : "-" }}</td>
          </tr>
          <tr class="highlight-edit">
            <td>{{ $t("Choose inspection mode") }}</td>
            <td :class="getCompareColor('inspectionMode')">
              <select :class="{'text-input': true}" v-model="measurement.inspectionMode" :disabled="isInputDisabled" @change="handleMeasurementChangeDelayed(measurement)">
                <option value="" disabled selected>{{ (route.name === 'Inspection Points' && selectedInspectionPoint && selectedInspectionPoint.inspectionMode) ? selectedInspectionPoint.inspectionMode : $t("Please Choose") }}</option>
                <option v-for="mode in actualCase.specs.inspectionModeTypes" :key="mode" :value="mode">{{ mode }}</option>
              </select>
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('inspectionMode') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.inspectionMode !== undefined && masterDefinitions.inspectionMode !== null && masterDefinitions.inspectionMode !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.inspectionMode))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.inspectionMode !== undefined && masterDefinitions.inspectionMode !== null && masterDefinitions.inspectionMode !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.inspectionMode : (selectedInspectionPoint ? selectedInspectionPoint.inspectionMode : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('inspectionMode') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.inspectionMode !== undefined && masterDefinitions.inspectionMode !== null && masterDefinitions.inspectionMode !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.inspectionMode))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.inspectionMode !== undefined && masterDefinitions.inspectionMode !== null && masterDefinitions.inspectionMode !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.inspectionMode : (selectedInspectionPoint ? selectedInspectionPoint.inspectionMode : '') }})
              </span>
            </td>
          </tr>
          <tr class="highlight-edit">
            <td>{{ $t("Choose inspection tool") }}</td>
            <td :class="getCompareColor('inspectionTool')">
              <select :class="{'text-input': true}" v-model="measurement.inspectionTool" :disabled="isInputDisabled" @change="handleMeasurementChangeDelayed(measurement)">
                <option value="" disabled selected>{{ (route.name === 'Inspection Points' && selectedInspectionPoint && selectedInspectionPoint.inspectionTool) ? selectedInspectionPoint.inspectionTool : $t("Please Choose") }}</option>
                <option v-for="mode in actualCase.specs.inspectionToolTypes" :key="mode" :value="mode">{{ mode }}</option>
              </select>
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('inspectionTool') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.inspectionTool !== undefined && masterDefinitions.inspectionTool !== null && masterDefinitions.inspectionTool !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.inspectionTool))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.inspectionTool !== undefined && masterDefinitions.inspectionTool !== null && masterDefinitions.inspectionTool !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.inspectionTool : (selectedInspectionPoint ? selectedInspectionPoint.inspectionTool : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('inspectionTool') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.inspectionTool !== undefined && masterDefinitions.inspectionTool !== null && masterDefinitions.inspectionTool !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.inspectionTool))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.inspectionTool !== undefined && masterDefinitions.inspectionTool !== null && masterDefinitions.inspectionTool !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.inspectionTool : (selectedInspectionPoint ? selectedInspectionPoint.inspectionTool : '') }})
              </span>
            </td>
          </tr>
          <!-- Maße -->
          <tr class="highlighted-row highlight-edit">
            <td>{{ $t("Enter actual dimension") }}</td>
            <td :class="getCompareColor('actualDimension')">
              <input
                  type="text"
                  v-model="measurement.actualDimension"
                  @input="handleInput('actualDimension', $event )"
                  @focus="handleFocus('actualDimension', $event)"
                  @blur="handleBlurField('actualDimension', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.actualDimension : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.actualDimension) }"
                  :disabled="isInputDisabled || measurement.inspectionMode !== 'Messen'"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('actualDimension') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.actualDimension !== undefined && masterDefinitions.actualDimension !== null && masterDefinitions.actualDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.actualDimension !== undefined && selectedInspectionPoint.actualDimension !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.actualDimension !== undefined && masterDefinitions.actualDimension !== null && masterDefinitions.actualDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.actualDimension : (selectedInspectionPoint ? selectedInspectionPoint.actualDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('actualDimension') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.actualDimension !== undefined && masterDefinitions.actualDimension !== null && masterDefinitions.actualDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.actualDimension))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.actualDimension !== undefined && masterDefinitions.actualDimension !== null && masterDefinitions.actualDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.actualDimension : (selectedInspectionPoint ? selectedInspectionPoint.actualDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('actualDimension')" class="deviation-value">
                Δ: {{ getDeviation('actualDimension') }}
              </span>
            </td>
          </tr>
          <!-- Select result gauge -->
          <tr class="highlighted-row highlight-edit">
            <td>{{ $t("Select result gauge") }}</td>
            <td :class="getCompareColor('resultGauge')">
              <select
                  :class="{'text-input': true}"
                  v-model="measurement.resultGauge"
                  :disabled="isInputDisabled || measurement.inspectionMode !== 'Lehren'"
                  @change="handleMeasurementChangeDelayed(measurement)"
              >
                <option value="" disabled selected>{{ (route.name === 'Inspection Points' && selectedInspectionPoint && selectedInspectionPoint.resultGauge) ? selectedInspectionPoint.resultGauge : $t("Please Choose") }}</option>
                <option v-for="mode in actualCase.specs.resultGaugeTypes" :key="mode" :value="mode">{{ mode }}</option>
              </select>
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('resultGauge') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.resultGauge !== undefined && masterDefinitions.resultGauge !== null && masterDefinitions.resultGauge !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.resultGauge))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.resultGauge !== undefined && masterDefinitions.resultGauge !== null && masterDefinitions.resultGauge !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.resultGauge : (selectedInspectionPoint ? selectedInspectionPoint.resultGauge : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('resultGauge') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.resultGauge !== undefined && masterDefinitions.resultGauge !== null && masterDefinitions.resultGauge !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.resultGauge))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.resultGauge !== undefined && masterDefinitions.resultGauge !== null && masterDefinitions.resultGauge !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.resultGauge : (selectedInspectionPoint ? selectedInspectionPoint.resultGauge : '') }})
              </span>
            </td>
          </tr>

          <!-- Enter nominal dimension -->
          <tr class="value-predefined">
            <td>{{ $t("Enter nominal dimension") }}</td>
            <td :class="getCompareColor('nominalDimension')">
              <input
                  type="text"
                  v-model="measurement.nominalDimension"
                  @input="handleInput('nominalDimension', $event)"
                  @focus="handleFocus('nominalDimension', $event)"
                  @blur="handleBlurField('nominalDimension', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.nominalDimension : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.nominalDimension) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('nominalDimension') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.nominalDimension !== undefined && masterDefinitions.nominalDimension !== null && masterDefinitions.nominalDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.nominalDimension !== undefined && selectedInspectionPoint.nominalDimension !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.nominalDimension !== undefined && masterDefinitions.nominalDimension !== null && masterDefinitions.nominalDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.nominalDimension : (selectedInspectionPoint ? selectedInspectionPoint.nominalDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('nominalDimension') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.nominalDimension !== undefined && masterDefinitions.nominalDimension !== null && masterDefinitions.nominalDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.nominalDimension))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.nominalDimension !== undefined && masterDefinitions.nominalDimension !== null && masterDefinitions.nominalDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.nominalDimension : (selectedInspectionPoint ? selectedInspectionPoint.nominalDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('nominalDimension')" class="deviation-value">
                Δ: {{ getDeviation('nominalDimension') }}
              </span>
            </td>
          </tr>

          <!-- Enter maximum dimension -->
          <tr class="value-predefined">
            <td>{{ $t("Enter maximum dimension") }}</td>
            <td :class="getCompareColor('maximumDimension')">
              <input
                  type="text"
                  v-model="measurement.maximumDimension"
                  @input="handleInput('maximumDimension', $event)"
                  @focus="handleFocus('maximumDimension', $event)"
                  @blur="handleBlurField('maximumDimension', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.maximumDimension : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.maximumDimension) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('maximumDimension') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.maximumDimension !== undefined && masterDefinitions.maximumDimension !== null && masterDefinitions.maximumDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.maximumDimension !== undefined && selectedInspectionPoint.maximumDimension !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.maximumDimension !== undefined && masterDefinitions.maximumDimension !== null && masterDefinitions.maximumDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.maximumDimension : (selectedInspectionPoint ? selectedInspectionPoint.maximumDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('maximumDimension') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.maximumDimension !== undefined && masterDefinitions.maximumDimension !== null && masterDefinitions.maximumDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.maximumDimension))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.maximumDimension !== undefined && masterDefinitions.maximumDimension !== null && masterDefinitions.maximumDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.maximumDimension : (selectedInspectionPoint ? selectedInspectionPoint.maximumDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('maximumDimension')" class="deviation-value">
                Δ: {{ getDeviation('maximumDimension') }}
              </span>
            </td>
          </tr>

          <!-- Enter minimum dimension -->
          <tr class="value-predefined">
            <td>{{ $t("Enter minimum dimension") }}</td>
            <td :class="getCompareColor('minimumDimension')">
              <input
                  type="text"
                  v-model="measurement.minimumDimension"
                  @input="handleInput('minimumDimension', $event)"
                  @focus="handleFocus('minimumDimension', $event)"
                  @blur="handleBlurField('minimumDimension', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.minimumDimension : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.minimumDimension) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('minimumDimension') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.minimumDimension !== undefined && masterDefinitions.minimumDimension !== null && masterDefinitions.minimumDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.minimumDimension !== undefined && selectedInspectionPoint.minimumDimension !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.minimumDimension !== undefined && masterDefinitions.minimumDimension !== null && masterDefinitions.minimumDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.minimumDimension : (selectedInspectionPoint ? selectedInspectionPoint.minimumDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('minimumDimension') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.minimumDimension !== undefined && masterDefinitions.minimumDimension !== null && masterDefinitions.minimumDimension !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.minimumDimension))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.minimumDimension !== undefined && masterDefinitions.minimumDimension !== null && masterDefinitions.minimumDimension !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.minimumDimension : (selectedInspectionPoint ? selectedInspectionPoint.minimumDimension : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('minimumDimension')" class="deviation-value">
                Δ: {{ getDeviation('minimumDimension') }}
              </span>
            </td>
          </tr>

          <!-- Enter upper tolerance -->
          <tr class="value-predefined">
            <td>{{ $t("Enter upper tolerance") }}</td>
            <td :class="getCompareColor('upperTolerance')">
              <input
                  type="text"
                  v-model="measurement.upperTolerance"
                  @input="handleInput('upperTolerance', $event)"
                  @focus="handleFocus('upperTolerance', $event)"
                  @blur="handleBlurField('upperTolerance', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.upperTolerance : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.upperTolerance) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('upperTolerance') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.upperTolerance !== undefined && masterDefinitions.upperTolerance !== null && masterDefinitions.upperTolerance !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.upperTolerance !== undefined && selectedInspectionPoint.upperTolerance !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.upperTolerance !== undefined && masterDefinitions.upperTolerance !== null && masterDefinitions.upperTolerance !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.upperTolerance : (selectedInspectionPoint ? selectedInspectionPoint.upperTolerance : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('upperTolerance') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.upperTolerance !== undefined && masterDefinitions.upperTolerance !== null && masterDefinitions.upperTolerance !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.upperTolerance))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.upperTolerance !== undefined && masterDefinitions.upperTolerance !== null && masterDefinitions.upperTolerance !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.upperTolerance : (selectedInspectionPoint ? selectedInspectionPoint.upperTolerance : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('upperTolerance')" class="deviation-value">
                Δ: {{ getDeviation('upperTolerance') }}
              </span>
            </td>
          </tr>

          <!-- Enter lower tolerance -->
          <tr class="value-predefined">
            <td>{{ $t("Enter lower tolerance") }}</td>
            <td :class="getCompareColor('lowerTolerance')">
              <input
                  type="text"
                  v-model="measurement.lowerTolerance"
                  @input="handleInput('lowerTolerance', $event)"
                  @focus="handleFocus('lowerTolerance', $event)"
                  @blur="handleBlurField('lowerTolerance', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.lowerTolerance : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.lowerTolerance) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('lowerTolerance') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.lowerTolerance !== undefined && masterDefinitions.lowerTolerance !== null && masterDefinitions.lowerTolerance !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.lowerTolerance !== undefined && selectedInspectionPoint.lowerTolerance !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.lowerTolerance !== undefined && masterDefinitions.lowerTolerance !== null && masterDefinitions.lowerTolerance !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.lowerTolerance : (selectedInspectionPoint ? selectedInspectionPoint.lowerTolerance : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('lowerTolerance') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.lowerTolerance !== undefined && masterDefinitions.lowerTolerance !== null && masterDefinitions.lowerTolerance !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.lowerTolerance))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.lowerTolerance !== undefined && masterDefinitions.lowerTolerance !== null && masterDefinitions.lowerTolerance !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.lowerTolerance : (selectedInspectionPoint ? selectedInspectionPoint.lowerTolerance : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('lowerTolerance')" class="deviation-value">
                Δ: {{ getDeviation('lowerTolerance') }}
              </span>
            </td>
          </tr>

          <!-- Enter tolerance value -->
          <tr class="value-predefined">
            <td>{{ $t("Enter tolerance value") }}</td>
            <td :class="getCompareColor('toleranceValue')">
              <input
                  type="text"
                  v-model="measurement.toleranceValue"
                  @input="handleInput('toleranceValue', $event)"
                  @focus="handleFocus('toleranceValue', $event)"
                  @blur="handleBlurField('toleranceValue', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.toleranceValue : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.toleranceValue) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('toleranceValue') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.toleranceValue !== undefined && masterDefinitions.toleranceValue !== null && masterDefinitions.toleranceValue !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.toleranceValue !== undefined && selectedInspectionPoint.toleranceValue !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.toleranceValue !== undefined && masterDefinitions.toleranceValue !== null && masterDefinitions.toleranceValue !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.toleranceValue : (selectedInspectionPoint ? selectedInspectionPoint.toleranceValue : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('toleranceValue') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.toleranceValue !== undefined && masterDefinitions.toleranceValue !== null && masterDefinitions.toleranceValue !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.toleranceValue))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.toleranceValue !== undefined && masterDefinitions.toleranceValue !== null && masterDefinitions.toleranceValue !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.toleranceValue : (selectedInspectionPoint ? selectedInspectionPoint.toleranceValue : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('toleranceValue')" class="deviation-value">
                Δ: {{ getDeviation('toleranceValue') }}
              </span>
            </td>
          </tr>

          <!-- Enter permissible measurement error -->
          <tr class="highlight-edit">
            <td>{{ $t("Enter permissible measurement error") }}</td>
            <td :class="getCompareColor('permissibleMeasurementError')">
              <input
                  type="text"
                  v-model="measurement.permissibleMeasurementError"
                  @input="handleInput('permissibleMeasurementError', $event)"
                  @focus="handleFocus('permissibleMeasurementError', $event)"
                  @blur="handleBlurField('permissibleMeasurementError', $event)"
                  :placeholder="(route.name === 'Inspection Points' && selectedInspectionPoint) ? selectedInspectionPoint.permissibleMeasurementError : ''"
                  :class="{ 'text-input': true, 'error-input': !validateFloat(measurement.permissibleMeasurementError) }"
                  :disabled="isInputDisabled"
              />
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('permissibleMeasurementError') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.permissibleMeasurementError !== undefined && masterDefinitions.permissibleMeasurementError !== null && masterDefinitions.permissibleMeasurementError !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && (selectedInspectionPoint.permissibleMeasurementError !== undefined && selectedInspectionPoint.permissibleMeasurementError !== null)))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.permissibleMeasurementError !== undefined && masterDefinitions.permissibleMeasurementError !== null && masterDefinitions.permissibleMeasurementError !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.permissibleMeasurementError : (selectedInspectionPoint ? selectedInspectionPoint.permissibleMeasurementError : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('permissibleMeasurementError') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.permissibleMeasurementError !== undefined && masterDefinitions.permissibleMeasurementError !== null && masterDefinitions.permissibleMeasurementError !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.permissibleMeasurementError))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.permissibleMeasurementError !== undefined && masterDefinitions.permissibleMeasurementError !== null && masterDefinitions.permissibleMeasurementError !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.permissibleMeasurementError : (selectedInspectionPoint ? selectedInspectionPoint.permissibleMeasurementError : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || (route.name === 'Ueben' && isUebenChecked)) && getDeviation('permissibleMeasurementError')" class="deviation-value">
                Δ: {{ getDeviation('permissibleMeasurementError') }}
              </span>
            </td>
          </tr>

          <tr class="highlighted-row highlight-edit">
            <td>{{ $t("Inspection point assessment") }}</td>
            <td :class="getCompareColor('inspectionPointAssessment')">
              <select :class="{'text-input': true}" v-model="measurement.inspectionPointAssessment" :disabled="isInputDisabled" @change="handleMeasurementChangeDelayed(measurement)">
                <option value="" disabled selected>{{ (route.name === 'Inspection Points' && selectedInspectionPoint && selectedInspectionPoint.inspectionPointAssessment) ? selectedInspectionPoint.inspectionPointAssessment : $t("Please Choose") }}</option>
                <option v-for="mode in actualCase.specs.inspectionPointAssessmentTypes" :key="mode" :value="mode">{{ mode }}</option>
              </select>
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('inspectionPointAssessment') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.inspectionPointAssessment !== undefined && masterDefinitions.inspectionPointAssessment !== null && masterDefinitions.inspectionPointAssessment !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.inspectionPointAssessment))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.inspectionPointAssessment !== undefined && masterDefinitions.inspectionPointAssessment !== null && masterDefinitions.inspectionPointAssessment !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.inspectionPointAssessment : (selectedInspectionPoint ? selectedInspectionPoint.inspectionPointAssessment : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('inspectionPointAssessment') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.inspectionPointAssessment !== undefined && masterDefinitions.inspectionPointAssessment !== null && masterDefinitions.inspectionPointAssessment !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.inspectionPointAssessment))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.inspectionPointAssessment !== undefined && masterDefinitions.inspectionPointAssessment !== null && masterDefinitions.inspectionPointAssessment !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.inspectionPointAssessment : (selectedInspectionPoint ? selectedInspectionPoint.inspectionPointAssessment : '') }})
              </span>
            </td>
          </tr>

          <tr class="highlighted-row highlight-edit">
            <td>{{ $t("Select rework") }}</td>
            <td :class="getCompareColor('rework')">
              <select :class="{'text-input': true}" v-model="measurement.rework" :disabled="isInputDisabled" @change="handleMeasurementChangeDelayed(measurement)">
                <option value="" disabled selected>{{ (route.name === 'Inspection Points' && selectedInspectionPoint && selectedInspectionPoint.rework) ? selectedInspectionPoint.rework : $t("Please Choose") }}</option>
                <option v-for="mode in actualCase.specs.reworkTypes" :key="mode" :value="mode">{{ mode }}</option>
              </select>
              <span v-if="(isUebenChecked || route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && (getCompareColor('rework') === 'red' || route.name === 'Inspection Points') && ((masterDefinitions && masterDefinitions.rework !== undefined && masterDefinitions.rework !== null && masterDefinitions.rework !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.rework))" class="correct-value">
                ({{ (masterDefinitions && masterDefinitions.rework !== undefined && masterDefinitions.rework !== null && masterDefinitions.rework !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.rework : (selectedInspectionPoint ? selectedInspectionPoint.rework : '') }})
              </span>
              <span v-if="(route.name === 'EvaluationDetail' || route.name === 'Inspection Points') && getCompareColor('rework') === 'green' && route.name !== 'Inspection Points' && ((masterDefinitions && masterDefinitions.rework !== undefined && masterDefinitions.rework !== null && masterDefinitions.rework !== '' && Object.keys(masterDefinitions).length > 0) || (selectedInspectionPoint && selectedInspectionPoint.rework))" class="correct-value green-text">
                ({{ (masterDefinitions && masterDefinitions.rework !== undefined && masterDefinitions.rework !== null && masterDefinitions.rework !== '' && Object.keys(masterDefinitions).length > 0) ? masterDefinitions.rework : (selectedInspectionPoint ? selectedInspectionPoint.rework : '') }})
              </span>
            </td>
          </tr>

          <tr v-if="route.name === 'Inspection Points'">
            <td colspan="2" class="hint-vorschlag-footer">
              * {{ $t('in Klammern Vorschlag des Wertes') }}
            </td>
          </tr>

          <tr v-if="route.name === 'Ueben'">
            <td colspan="2" class="text-center button-container">
               <v-btn color="#005AA9" @click="checkUeben" class="white--text mr-2">
                  {{ $t('Check') }}
               </v-btn>
               <v-btn color="grey lighten-1" @click="resetUeben" class="white--text">
                  {{ $t('Reset') }}
               </v-btn>
            </td>
          </tr>

          <tr v-if="route.name === 'Test'">
            <td colspan="2" class="text-center button-container">
               <v-btn color="success" @click="finishTestDialog = true" :disabled="isTestFinished" class="white--text">
                  <v-icon start>mdi-flag-checkered</v-icon>
                  {{ $t('Prüfung abschließen') }}
               </v-btn>
            </td>
          </tr>

        </table>
      </div>
      <!-- Rechter Bereich -->
      <div class="area right">
        <div class="right-content top">
          <v-col>
            <v-row class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center ga-2">
                <h4>{{ $t("Workpiece Drawing") }}</h4>
                <v-btn
                  v-if="selectedWorkpiece && selectedWorkpiece.image"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="toggleDrawingPreviewVariant"
                >
                  {{ drawingPreviewToggleLabel }}
                </v-btn>
              </div>
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-if="selectedWorkpiece && selectedWorkpiece.drawing"
                    v-bind="props"
                    icon="mdi-arrow-expand-all"
                    variant="text"
                    color="primary"
                    size="small"
                    @click="openCurrentDrawing"
                    class="s3m-expand-button"
                  ></v-btn>
                </template>
                <span>{{ openCurrentDrawingTooltip }}</span>
              </v-tooltip>
            </v-row>
            <v-row>
              <div class="image-container">
                <v-img :src="drawingImageUrl" class="image-full" ref="drawingImageRef">
                  <template v-slot:default>
                    <!-- Unsichtbares IMG zum Messen der tatsächlichen skalierten Größe -->
                    <img
                        alt="invisible"
                      :src="drawingImageUrl" 
                      style="width: 100%; height: 100%; object-fit: contain; opacity: 0; position: absolute;" 
                      @load="handleDrawingImageLoad"
                    />
                    <!-- Container für die Marker, der durch Flexbox zentriert wird -->
                    <div class="marker-drawing-area">
                      <div class="marker-container-inner" :style="markerContainerInnerStyle" ref="markerContainerRef">
                        <div
                            v-for="inspectionPoint in inspectionPoints"
                            @click.stop="onInspectionPointClick(inspectionPoint)"
                            :key="inspectionPoint._id"
                          class="invisible-rectangle"
                          :class="{
                            'active-marker': selectedInspectionPoint && selectedInspectionPoint._id === inspectionPoint._id,
                            'editable-marker': route.name === 'Inspection Points' && canEditMarkers && selectedInspectionPoint && selectedInspectionPoint._id === inspectionPoint._id
                          }"
                          :style="getMarkerStyle(inspectionPoint)"
                          @mousedown="route.name === 'Inspection Points' && canEditMarkers && selectedInspectionPoint && selectedInspectionPoint._id === inspectionPoint._id ? onMarkerMouseDown($event) : null"
                        >
                          <div
                            v-if="route.name === 'Inspection Points' && canEditMarkers && selectedInspectionPoint && selectedInspectionPoint._id === inspectionPoint._id"
                            class="marker-resize-handle"
                            @mousedown.stop.prevent="onResizeMouseDown($event)"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </template>
                </v-img>
              </div>
            </v-row>
            <v-row v-if="route.name === 'Inspection Points' && canEditMarkers && isAdmin && selectedInspectionPoint && selectedInspectionPoint._id">
              <div class="d-flex justify-end w-100 mt-1 px-2">
                <v-btn
                  color="error"
                  size="small"
                  variant="elevated"
                  :loading="isSavingMarker"
                  @click="saveMarker"
                >
                  <v-icon start>mdi-map-marker-check</v-icon>
                  {{ $t('Marker speichern') }}
                </v-btn>
              </div>
            </v-row>
          </v-col>
        </div>
        <div class="right-content bottom">
          <v-col>
            <v-row>
              <div class="d-flex align-center justify-space-between w-100">
                <h4>{{ $t("Inspection Proposal") }}</h4>
                <v-btn
                  v-if="route.name === 'Ueben' && !showSuggestion"
                  color="primary"
                  size="small"
                  @click="showSuggestion = true"
                >
                  {{ $t("Show Inspection Proposal") }}
                </v-btn>
              </div>
            </v-row>
            <v-row v-if="shouldRenderSuggestionArea">
              <div class="image-container">
                <v-img
                  v-if="suggestionImageUrl && !suggestionImageFailed"
                  :src="suggestionImageUrl"
                  class="image-full"
                  @error="handleSuggestionImageError"
                >
                  <!-- Add/Remove Icons for current selection -->
                  <div v-if="route.name === 'Inspection Points'" class="suggestion-action-overlay">
                    <v-btn
                      v-if="!isImageAdded(currentSelectedUploadId)"
                      class="action-btn-large success"
                      @click.stop="addProposalImage(currentSelectedUploadId)"
                      :title="$t('Als Prüfvorschlagbild hinzufügen')"
                    >
                      <v-icon size="32">mdi-plus</v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      class="action-btn-large error"
                      @click.stop="removeProposalImage(currentSelectedUploadId)"
                      :title="$t('Aus Prüfvorschlagbildern entfernen')"
                    >
                      <v-icon size="32">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </v-img>
                <div v-else-if="route.name === 'Inspection Points'" class="suggestion-empty-state">
                  <v-icon size="48" color="grey-lighten-1">mdi-image-off-outline</v-icon>
                  <div class="text-caption text-grey mt-2">{{ $t('Kein Prüfvorschlagbild verfügbar') }}</div>
                </div>
              </div>
            </v-row>
            <v-row v-if="showProposalGallery" class="mt-2">
              <div class="uploads-gallery">
                <!-- Original Image Option -->
                <div 
                  v-if="defaultSuggestionImageUrl && (route.name === 'Inspection Points' || isImageAdded('original'))"
                  class="upload-thumb"
                  :class="{ 
                    'active': isSelectedUpload('original'),
                    'is-added': route.name === 'Inspection Points' && isImageAdded('original'),
                    'not-added': route.name === 'Inspection Points' && !isImageAdded('original')
                  }"
                  @click="selectUploadAsSuggestion('original')"
                >
                  <v-img :src="defaultSuggestionImageUrl" cover class="thumb-img">
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                </div>
                <!-- Other Uploads -->
                <div 
                  v-for="upload in imageUploads" 
                  :key="upload.file.id" 
                  v-show="shouldRenderUploadThumb(upload)"
                  class="upload-thumb"
                  :class="{ 
                    'active': isSelectedUpload(upload),
                    'is-added': route.name === 'Inspection Points' && isImageAdded(upload.file.id),
                    'not-added': route.name === 'Inspection Points' && !isImageAdded(upload.file.id)
                  }"
                  @click="selectUploadAsSuggestion(upload)"
                >
                  <v-img v-if="getThumbUrlEffective(upload)" :src="getThumbUrlEffective(upload)" cover class="thumb-img">
                    <template v-slot:placeholder>
                      <v-row v-if="route.name === 'Inspection Points'" class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <div v-else-if="route.name === 'Inspection Points'" class="thumb-missing">
                    <v-icon size="20" color="grey-lighten-1">mdi-image-off-outline</v-icon>
                  </div>
                </div>
              </div>
            </v-row>
          </v-col>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, defineProps, onMounted, ref, watch} from 'vue';
import APIServiceRestDB from "@/services/MesskursBackendService";
import {useStore} from 'vuex'; // Importieren Sie den Store
import {useRoute, useRouter} from 'vue-router'; // Importieren Sie useRoute
import {
  assetNamesMatch,
  convertJsonArraysToJs,
  floatsToLocal,
  getAssetUrl,
  getAuthenticatedFileUrl,
  getFileTokenUrl,
  isMesskursCourse,
  normalizeAssetFilename,
  reformatFloats
} from '@/tools/utils'
import APIService from "@/services/APIService";

// Reaktive Referenzen für Dropdown-Arrays
const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  }
});
const store = useStore(); // Verwenden Sie den Store
const route = useRoute(); // Verwenden Sie useRoute
const actualCase = ref({
  specs: {
    inspectionModeTypes: [],
    inspectionToolTypes: []
  },
  parameters: {}
    });
const workpieces = ref([]);
const workpieceSelect = ref(null);
const inspectionPoints = ref([]);
const inspectionPointSelect = ref(null);
const extraCourses = ref([]);
const loadingExtraCourses = ref(false);

async function fetchExtraCoursesForObject(objectId) {
  if (route.name !== 'Inspection Points') return;
  
  loadingExtraCourses.value = true;
  extraCourses.value = [];
  try {
    const courseIds = new Set();
    const objectCourseIds = [
      actualCase.value.parameters.course,
      actualCase.value.parameters.courseId,
      actualCase.value.parameters.cLearningCourse,
      actualCase.value.parameters.cLearningCourseId
    ].filter(Boolean);
    objectCourseIds.forEach(id => courseIds.add(id));

    const sessions = await APIServiceRestDB.getSessionsForObject(objectId);
    if (sessions && sessions.length > 0) {
      sessions.map(s => s.course).filter(Boolean).forEach(id => courseIds.add(id));
    }

    let loadedCourses = [];
    if (courseIds.size > 0) {
      loadedCourses = (await Promise.all([...courseIds].map(id => APIService.getCourse(id, true))))
        .filter(c => !!c && c !== 404);
    }

    if (!loadedCourses.some(course => Array.isArray(course.uploads) && course.uploads.length > 0)) {
      const userId = store.state.user?.id || store.state.user?._id;
      if (userId) {
        const userCoursesResponse = await APIService.getUserCourses(userId).catch(() => []);
        const userCourses = Array.isArray(userCoursesResponse)
          ? userCoursesResponse
          : (userCoursesResponse?.results || []);
        const matchingCourses = userCourses.filter(course => isMesskursCourse(course));
        const fallbackCourses = matchingCourses.length > 0 ? matchingCourses : userCourses;
        const fallbackIds = fallbackCourses
          .map(course => course.id || course._id)
          .filter(id => id && !courseIds.has(id));
        const fallbackLoaded = (await Promise.all(fallbackIds.map(id => APIService.getCourse(id, true))))
          .filter(c => !!c && c !== 404 && Array.isArray(c.uploads) && c.uploads.length > 0);
        loadedCourses = [...loadedCourses, ...fallbackLoaded];
      }
    }

    const seen = new Set();
    extraCourses.value = loadedCourses.filter(course => {
      const id = course.id || course._id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  } catch (e) {
    console.error("Error fetching extra courses:", e);
  } finally {
    loadingExtraCourses.value = false;
  }
}
const isTestFinished = computed(() => {
  return store.state.session?.testFinished === true;
});
const showFinishedTestBannerForTrainee = computed(() => {
  return isTestFinished.value && route.name === 'Test';
});

const isInputDisabled = computed(() => {
  if (props.readOnly) return true;
  if (route.name === 'Inspection Points') return false;
  if (route.name === 'Lernen') return true;
  if (route.name === 'Test' && isTestFinished.value) return true;
  // In anderen Modi (Ueben, Lernen) ist die Eingabe trotz abgeschlossener Prüfung erlaubt
  return route.name !== 'Test' && route.name !== 'Ueben' && route.name !== 'Lernen';

});
const finishTestDialog = ref(false);
const isFinishing = ref(false);
const testFinishedSnackbar = ref(false);
const reenableTestDialog = ref(false);
const isReenabling = ref(false);

async function confirmFinishTest() {
  isFinishing.value = true;
  try {
    // Falls noch ein Save-Timeout läuft, diesen sofort ausführen
    if (timeoutId) {
      clearTimeout(timeoutId);
      const measurementToSave = JSON.parse(JSON.stringify(measurement.value));
      const pointIdToSave = selectedInspectionPoint.value._id;
      await handleMeasurementChange(measurementToSave, pointIdToSave);
    }

    // Refresh session again before finish to be sure it's not already finished
    const freshSession = await APIServiceRestDB.getSession(store.state.sessionId);
    if (freshSession && freshSession.testFinished) {
      store.commit('setSession', freshSession);
      testFinishedSnackbar.value = true;
      finishTestDialog.value = false;
      return;
    }

    const result = await APIServiceRestDB.finishSession(store.state.sessionId);
    if (result) {
      // Session im Store aktualisieren
      const updatedSession = { ...store.state.session, testFinished: true };
      store.commit('setSession', updatedSession);
      testFinishedSnackbar.value = true;
      finishTestDialog.value = false;
    }
  } catch (e) {
    console.error("Error finishing test:", e);
  } finally {
    isFinishing.value = false;
  }
}

async function confirmReenableTest() {
  isReenabling.value = true;
  try {
    const result = await APIServiceRestDB.reenableSession(store.state.sessionId);
    if (result) {
      // Session im Store aktualisieren
      const updatedSession = { ...store.state.session, testFinished: false };
      store.commit('setSession', updatedSession);
      reenableTestDialog.value = false;
    }
  } catch (e) {
    console.error("Error re-enabling test:", e);
  } finally {
    isReenabling.value = false;
  }
}

const router = useRouter();
const modeNotEnabled = computed(() => {
  const session = store.state.session;
  if (!session || route.name === 'Inspection Points' || route.name === 'EvaluationDetail') {
    return false;
  }
  
  if (route.name === 'Lernen' && !session.study) return true;
  if (route.name === 'Ueben' && !session.practice) return true;
  return route.name === 'Test' && !session.test;
  

});
const activeSessionName = computed(() => {
  const session = store.state.session;
  if (session) {
    return session.title || session.caseTitle || (session.object && typeof session.object === 'string' ? session.object : null);
  }
  return null;
});
const title = ref("");
const selectedWorkpiece = ref({});
const selectedWorkpieceId = ref("");
const selectedInspectionPoint = ref({});
const searchQuery = ref("");
const showSearchResults = ref(false);
const isUebenChecked = ref(false);
const showSuggestion = ref(false);
const drawingImageUrl = ref('');
const showIsoGpsPreview = ref(false);
const suggestionImageUrl = ref('');
const suggestionImageFailed = ref(false);
const defaultSuggestionImageUrl = ref('');
const proposalImages = ref([]);

const drawingImageRef = ref(null);
const drawingImageSize = ref({ width: 0, height: 0, naturalWidth: 1, naturalHeight: 1 });
const markerContainerRef = ref(null);
const editableMarker = ref([5, 5, 10, 10]);
const isDragging = ref(false);
const isResizing = ref(false);
const dragStartData = ref({ mouseX: 0, mouseY: 0, markerX: 0, markerY: 0, markerW: 0, markerH: 0 });
const isSavingMarker = ref(false);
const canManageInspectionPoints = computed(() => {
  const user = store.state.user;
  return user?.role === 'admin'
    || user?.role === 'licenceManager'
    || user?.hasTeamLicences === true
    || user?.hasFrontendManagedLicences === true
    || user?.messkursInstructorAccess === true;
});
const markerEditingEnabled = computed(() =>
  String(process.env.VUE_APP_ENABLE_MARKER_EDIT || '').toLowerCase() === 'true'
);
const canEditMarkers = computed(() => markerEditingEnabled.value && canManageInspectionPoints.value);
const isAdmin = computed(() => store.state.user?.role === 'admin');
const hasDragged = ref(false);

const drawingPreviewToggleLabel = computed(() => (
  showIsoGpsPreview.value ? 'Techn. Zeichnung anzeigen' : 'ISO-GPS anzeigen'
));

const openCurrentDrawingTooltip = computed(() => (
  showIsoGpsPreview.value ? 'ISO-GPS-Zeichnung öffnen' : 'Technische Zeichnung öffnen'
));

const activeMarkerField = computed(() => (showIsoGpsPreview.value ? 'marker-gps' : 'marker'));

function setSuggestionImageUrl(url) {
  suggestionImageFailed.value = false;
  suggestionImageUrl.value = url || '';
}

function handleSuggestionImageError() {
  suggestionImageFailed.value = true;
}

const handleDrawingImageLoad = (event) => {
  const img = event.target;
  drawingImageSize.value = {
    width: img.clientWidth,
    height: img.clientHeight,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight
  };
};

// Berechnet die tatsächliche Größe des Bildes innerhalb des v-img (contain)
const markerContainerInnerStyle = computed(() => {
  if (!drawingImageSize.value.naturalWidth) return { width: '100%', height: '100%' };

  const containerWidth = drawingImageSize.value.width;
  const containerHeight = drawingImageSize.value.height;
  const imgRatio = drawingImageSize.value.naturalWidth / drawingImageSize.value.naturalHeight;
  const containerRatio = containerWidth / containerHeight;

  let actualWidth, actualHeight;

  if (imgRatio > containerRatio) {
    // Bild ist breiter als der Container (relativ) -> Breite füllt aus
    actualWidth = containerWidth;
    actualHeight = containerWidth / imgRatio;
  } else {
    // Bild ist höher als der Container (relativ) -> Höhe füllt aus
    actualHeight = containerHeight;
    actualWidth = containerHeight * imgRatio;
  }

  return {
    width: `${actualWidth}px`,
    height: `${actualHeight}px`,
    position: 'relative'
  };
});
const courseUploads = computed(() => {
  let uploads = [];
  
  // Abhängigkeit von proposalImages explizit tracken
  const currentProposals = proposalImages.value || [];
  
  // Abhängigkeit von store.state.course explizit tracken
  const currentCourse = store.state.course;
  
  if (currentCourse && currentCourse.uploads) {
    uploads = [...currentCourse.uploads];
  }

  if (route.name === 'Inspection Points') {
    extraCourses.value.forEach(c => {
      if (c.uploads) {
        c.uploads.forEach(u => {
          if (!uploads.find(existing => String(existing.file?.id) === String(u.file?.id))) {
            uploads.push(u);
          }
        });
      }
    });
  }

  // Ergänze Uploads aus proposalImages, falls sie noch nicht in der Liste sind
  if (currentProposals.length > 0) {
    currentProposals.forEach(pi => {
      if (pi.fileId && pi.fileId !== 'original') {
        const existing = uploads.find(u => String(u.file?.id) === String(pi.fileId));
        if (!existing) {
          uploads.push({
            file: {
              id: pi.fileId,
              originalName: pi.originalName || pi.path
            },
            path: pi.path,
            description: pi.description
          });
        } else if (!existing.path && pi.path) {
          existing.path = pi.path;
        }
      }
    });
  }

  return uploads;
});
const imageUploads = computed(() => {
  // Debugging-Log für den Fehlerzustand (optional, zeigt jetzt nur Zusammenfassung)
  // console.log("Re-calculating imageUploads, items in courseUploads:", courseUploads.value.length);
  
  return courseUploads.value.filter(u => {
    const file = u.file || {};
    const nameToTest = file.originalName || file.name || file.filename || file.path || u.path || "";
    const mimeType = file.mimeType || file.mimetype || file.type || u.mimeType || u.type || "";
    
    const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(nameToTest) || String(mimeType).startsWith('image/');
    
    return !!file.id && isImg;
  });
});

const showProposalGallery = computed(() => {
  const modeAllowsGallery =
    route.name === 'Inspection Points'
    || (route.name !== 'Test' && route.name !== 'Ueben')
    || (route.name === 'Ueben' && showSuggestion.value)
    || (route.name === 'Test' && showSuggestion.value);

  return modeAllowsGallery && (imageUploads.value.length > 0 || !!defaultSuggestionImageUrl.value);
});

const shouldRenderSuggestionArea = computed(() => {
  const modeAllowsSuggestion =
    (route.name !== 'Test' && route.name !== 'Ueben' && !props.readOnly)
    || (route.name === 'Ueben' && showSuggestion.value)
    || props.readOnly;

  return modeAllowsSuggestion && (
    route.name === 'Inspection Points'
    || (!!suggestionImageUrl.value && !suggestionImageFailed.value)
  );
});

function shouldRenderUploadThumb(upload) {
  if (route.name === 'Inspection Points') return true;
  return isImageAdded(upload.file.id) && !!getThumbUrlEffective(upload);
}

function isContentPath(path) {
  return !!path && !/^https?:\/\//i.test(path) && !path.startsWith('blob:') && path.includes('/');
}

function findUploadByName(uploadName, defaultExtension) {
  if (!uploadName || !store.state.course?.uploads) return null;
  return store.state.course.uploads.find((u) => {
    if (!u?.file) return false;
    return assetNamesMatch(u.file.originalName, uploadName, defaultExtension)
      || assetNamesMatch(u.path, uploadName, defaultExtension);
  }) || null;
}

function resolveIsoGpsVariantFilename(filename, defaultExtension, useIsoGps) {
  const normalized = normalizeAssetFilename(filename, defaultExtension)
    .replace(/-ISO-(GSP|PGS)(?=\.|$)/i, '-ISO-GPS');

  if (!useIsoGps) {
    return normalized.replace(/-ISO-GPS(?=\.|$)/i, '');
  }

  if (/-ISO-GPS(?=\.|$)/i.test(normalized)) {
    return normalized;
  }

  const extensionMatch = normalized.match(/\.([^./?#]+)(?=[?#]|$)/i);
  const extension = extensionMatch?.[1] || defaultExtension;
  const withoutExtension = normalized.replace(/\.[^./?#]+(?=[?#]|$)/i, '');
  return `${withoutExtension}-ISO-GPS.${extension}`;
}

async function updateDrawingImageUrl() {
  if (!selectedWorkpiece.value?.image) {
    drawingImageUrl.value = '';
    return;
  }

  const imageFileName = resolveIsoGpsVariantFilename(
    selectedWorkpiece.value.image,
    'png',
    showIsoGpsPreview.value
  );

  let imageUrl = getAssetUrl(
    imageFileName,
    actualCase.value.specs.number || actualCase.value.parameters.number || '0',
    'wp',
    selectedWorkpiece.value.id || selectedWorkpiece.value.number || '0',
    'png'
  );

  const upload = findUploadByName(imageFileName, 'png');
  if (upload?.file) {
    const tokenUrl = await getFileTokenUrl(APIService, upload.file.id);
    if (tokenUrl) imageUrl = tokenUrl;
  }

  drawingImageUrl.value = imageUrl;
}

async function toggleDrawingPreviewVariant() {
  showIsoGpsPreview.value = !showIsoGpsPreview.value;
  await updateDrawingImageUrl();
}

function parseMarkerValue(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  return null;
}

function getMarkerForMode(checkpoint, useIsoGps = showIsoGpsPreview.value) {
  if (!checkpoint) return null;
  if (useIsoGps) {
    return (
      parseMarkerValue(checkpoint['marker-gps'])
      || parseMarkerValue(checkpoint.markerGps)
      || parseMarkerValue(checkpoint.marker_gps)
      || null
    );
  }
  return parseMarkerValue(checkpoint.marker);
}

async function resolveProposalImageUrl(image) {
  if (!image) return defaultSuggestionImageUrl.value;
  if (image.fileId === 'original') return defaultSuggestionImageUrl.value;
  if (image.path && /^https?:\/\//i.test(image.path)) return image.path;

  if (image.path && isContentPath(image.path)) {
    const token = store.state.user?.tokens?.access?.token;
    const blobUrl = await getAuthenticatedFileUrl(image.path, token);
    if (blobUrl) return blobUrl;
  }

  if (image.fileId) {
    const tokenUrl = await getFileTokenUrl(APIService, image.fileId);
    if (tokenUrl) return tokenUrl;
  }

  if (image.path) {
    return getAssetUrl(
      image.path,
      actualCase.value.specs.number || actualCase.value.parameters.number || '0',
      'wp',
      selectedWorkpiece.value.id || selectedWorkpiece.value.number || '0',
      'png'
    );
  }

  return defaultSuggestionImageUrl.value;
}

const filteredSearchResults = computed(() => {
  if (!searchQuery.value) {
    return [];
  }
  const query = searchQuery.value.toLowerCase();
  return filteredInspectionPoints.value.filter(item =>
      (item.title && item.title.toLowerCase().includes(query)) ||
      (item.number && item.number.toString().toLowerCase().includes(query))
  );
});

const filteredInspectionPoints = computed(() => {
  // AUSNAHME: Im Modus "Inspection Points" (Einrichten) werden immer alle Prüfstellen angezeigt
  if (store.state.session && store.state.session.items && store.state.session.items.length > 0 && route.name !== 'Inspection Points') {
    // Wenn eine Sitzung mit items aktiv ist, filtere die Prüfstellen
    const sessionItems = store.state.session.items;
    const filtered = inspectionPoints.value.filter(ip => 
      sessionItems.some(item => item._id === ip._id)
    );
    // Falls die Filterung Ergebnisse liefert, verwenden wir diese. 
    // Andernfalls (z.B. Session-Items passen nicht zum Werkstück) zeigen wir alle an.
    if (filtered.length > 0) {
      return filtered;
    }
  }
  return inspectionPoints.value;
});

const isPointProcessed = computed(() => {
  if (route.name !== 'EvaluationDetail' && route.name !== 'Test') return true;
  const resultsStr = store.state.session?.results;
  if (!resultsStr) return false;
  try {
    const results = JSON.parse(resultsStr);
    return !!results[selectedInspectionPoint.value._id];
  } catch (e) {
    return false;
  }
});

const measurement = ref({
  user: "",
  toolboxId: "",
  checkpointId: "",
  inspectionMode: "",
  inspectionTool: "",
  actualDimension: null,
  actualDimension_old: null,
  resultGauge: "Gut",
  resultGauge_old: null,
  nominalDimension: null,
  nominalDimension_old: null,
  maximumDimension: null,
  maximumDimension_old: null,
  minimumDimension: null,
  minimumDimension_old: null,
  upperTolerance: null,
  upperTolerance_old: null,
  lowerTolerance: null,
  lowerTolerance_old: null,
  toleranceValue: null,
  toleranceValue_old: null,
  permissibleMeasurementError: null,
  permissibleMeasurementError_old: null,
  inspectionPointAssessment: "Gut",
  rework: "Nicht erforderlich",
  sessionId: null
});

// Funktion zum Festlegen der Standardwerte für measurement
function setDefaultMeasurementValues(forceEmpty = false) {
  measurement.value.checkpointId = selectedInspectionPoint.value._id;
  measurement.value.user = actualCase.value.parameters.owner;
  measurement.value.toolboxId = actualCase.value.parameters._id;
  measurement.value.sessionId = store.state.sessionId;

  if (route.name === 'Test' || route.name === 'Ueben' || route.name === 'Inspection Points' || forceEmpty) {
    measurement.value.inspectionMode = "";
    measurement.value.inspectionTool = "";

    measurement.value.actualDimension = "";
    measurement.value.actualDimension_old = 0;
    measurement.value.resultGauge = "";
    measurement.value.resultGauge_old = "";
    measurement.value.nominalDimension = "";
    measurement.value.nominalDimension_old = 0;
    measurement.value.maximumDimension = "";
    measurement.value.maximumDimension_old = 0;
    measurement.value.minimumDimension = "";
    measurement.value.minimumDimension_old = 0;
    measurement.value.upperTolerance = "";
    measurement.value.upperTolerance_old = 0;
    measurement.value.lowerTolerance = "";
    measurement.value.lowerTolerance_old = 0;
    measurement.value.toleranceValue = "";
    measurement.value.toleranceValue_old = 0;
    measurement.value.permissibleMeasurementError = "";
    measurement.value.permissibleMeasurementError_old = 0;
    measurement.value.inspectionPointAssessment = "";
    measurement.value.rework = "";
  } else {
    measurement.value.inspectionMode = "Messen";
    measurement.value.inspectionTool = "Messschieber";

    measurement.value.actualDimension = 0;
    measurement.value.actualDimension_old = 0;
    measurement.value.resultGauge = "Gut";
    measurement.value.resultGauge_old = 0;
    measurement.value.nominalDimension = selectedInspectionPoint.value.nominalDimension;
    measurement.value.nominalDimension_old = 0;
    measurement.value.maximumDimension = selectedInspectionPoint.value.maximumDimension;
    measurement.value.maximumDimension_old = 0;
    measurement.value.minimumDimension = selectedInspectionPoint.value.minimumDimension;
    measurement.value.minimumDimension_old = 0;
    measurement.value.upperTolerance = selectedInspectionPoint.value.upperTolerance;
    measurement.value.upperTolerance_old = 0;
    measurement.value.lowerTolerance = selectedInspectionPoint.value.lowerTolerance;
    measurement.value.lowerTolerance_old = 0;
    measurement.value.toleranceValue = selectedInspectionPoint.value.toleranceValue;
    measurement.value.toleranceValue_old = 0;
    measurement.value.permissibleMeasurementError = 0;
    measurement.value.permissibleMeasurementError_old = 0;
    measurement.value.inspectionPointAssessment = "Gut";
    measurement.value.rework = "Nicht erforderlich";
  }
  measurement.value._id = null;
}
async function getMeasurementDefinitionsForCase(){
  isLoadingProposalImages.value = true;
  
  isUebenChecked.value = false;
  showSuggestion.value = false;
  selectedUploadId.value = null; // Zurücksetzen der Auswahl beim Laden eines neuen Punkts
  proposalImages.value = []; // Reset proposal images to ensure clean state

  // Wir stellen sicher, dass wir bei jedem Punkt-Wechsel mit sauberen Standardwerten starten.
  // In EvaluationDetail, Test und Ueben erzwingen wir leere Werte,
  // in Inspection Points (Einrichten) verwenden wir die Standard-Vorbelegungen.
  const shouldForceEmpty = (route.name === 'EvaluationDetail' || route.name === 'Test' || route.name === 'Ueben' || route.name === 'Inspection Points');
  setDefaultMeasurementValues(shouldForceEmpty);

  if (route.name === 'EvaluationDetail' || route.name === 'Test' || route.name === 'Inspection Points') {
    masterDefinitions.value = await APIServiceRestDB.getCheckpointSettings(
      actualCase.value.parameters._id, 
      selectedInspectionPoint.value._id
    );
    if (masterDefinitions.value && Object.keys(masterDefinitions.value).length > 0) {
      floatsToLocal(masterDefinitions.value);
      // Im Einrichten-Modus (Inspection Points) füllen wir die Werte direkt ab, falls vorhanden
      if (route.name === 'Inspection Points') {
        Object.assign(measurement.value, masterDefinitions.value);
      }
    }

    // Resultate aus der Session laden
    if (route.name === 'EvaluationDetail' || route.name === 'Test') {
      let sessionData = store.state.session;
      // Wir holen uns die Session IMMER frisch vom Server, um den aktuellsten Stand zu haben (testFinished etc.)
      const sessId = store.state.sessionId || route.params.sessionId;
      if (sessId) {
        try {
          sessionData = await APIServiceRestDB.getSession(sessId);
          if (sessionData) {
            store.commit('setSession', sessionData);
          }
        } catch (e) {
          console.error("Error refreshing session from server:", e);
        }
      }

      const sessionResultsStr = sessionData?.results;
      if (sessionResultsStr) {
        try {
          const sessionResults = JSON.parse(sessionResultsStr);
          const currentPointId = selectedInspectionPoint.value._id;
          const currentPointResult = sessionResults[currentPointId];
          
          if (currentPointResult) {
            floatsToLocal(currentPointResult);
            Object.assign(measurement.value, currentPointResult);
            console.log("Loaded result for point from session:", currentPointResult);
          }
        } catch (e) {
          console.error("Error parsing session results:", e);
        }
      }
    }
  }

  if (route.name === 'Lernen' || route.name === 'Ueben') {
    const definitions = await APIServiceRestDB.getCheckpointSettings(
      actualCase.value.parameters._id, 
      selectedInspectionPoint.value._id
    );
    if (definitions && Object.keys(definitions).length > 0) {
      floatsToLocal(definitions);
      masterDefinitions.value = definitions;
      if (route.name === 'Lernen') {
        Object.assign(measurement.value, definitions);
      }
    } else {
      masterDefinitions.value = null;
    }
  }

  // WICHTIG: proposalImages müssen für ALLE Modi geladen werden
  // Wir nutzen masterDefinitions, falls sie oben bereits geladen wurden (spart einen API-Call)
  // Wir übergeben { skipOverlay: true } an APIServiceRestDB, falls unterstützt, 
  // oder stellen sicher, dass der Aufruf asynchron im Hintergrund läuft.
  const newMeasurementValues = masterDefinitions.value || await APIServiceRestDB.getCheckpointSettings(actualCase.value.parameters._id, selectedInspectionPoint.value._id);
  
  if (newMeasurementValues && newMeasurementValues.proposalImages) {
    try {
      let rawProposalImages = newMeasurementValues.proposalImages;
      
      // Fehlerkorrektur für fehlerhaftes JSON-Format (Punkt statt Komma zwischen Properties)
      // Beispiel: "fileId":"..."."originalName":"..."
      if (typeof rawProposalImages === 'string' && rawProposalImages.includes('"."')) {
        rawProposalImages = rawProposalImages.replace(/"\."/g, '","');
      }

      const parsedImages = typeof rawProposalImages === 'string' 
        ? JSON.parse(rawProposalImages) 
        : rawProposalImages;
      
      // Heilung von korrumpierten Daten (Komma statt Punkt in Dateinamen/Pfaden)
      let healedImages = [];
      if (Array.isArray(parsedImages)) {
        healedImages = parsedImages.map(img => {
          if (img.path && img.path.includes(',png')) img.path = img.path.replace(',png', '.png');
          if (img.path && img.path.includes(',jpg')) img.path = img.path.replace(',jpg', '.jpg');
          if (img.path && img.path.includes(',jpeg')) img.path = img.path.replace(',jpeg', '.jpeg');
          if (img.originalName && img.originalName.includes(',png')) img.originalName = img.originalName.replace(',png', '.png');
          if (img.originalName && img.originalName.includes(',jpg')) img.originalName = img.originalName.replace(',jpg', '.jpg');
          if (img.originalName && img.originalName.includes(',jpeg')) img.originalName = img.originalName.replace(',jpeg', '.jpeg');
          return img;
        });
      }

      // Zuweisung an das reaktive Ref (Löst Computed Properties aus)
      proposalImages.value = healedImages;
      
      console.log("Loaded proposalImages from DB:", proposalImages.value);

      // Falls wir im proposalImages-Array sind, setzen wir das erste Bild als aktiv
      if (proposalImages.value.length > 0) {
        // PRIORITÄT: Wenn wir im Lernen/Üben Modus sind, versuchen wir erst das Originalbild zu finden
        const originalImage = proposalImages.value.find(img => img.fileId === 'original');
        const firstImage = originalImage || proposalImages.value[0];
        
        selectedUploadId.value = firstImage.fileId;
        resolveProposalImageUrl(firstImage).then(url => {
          setSuggestionImageUrl(url);
        });
      } else {
        // Fallback auf original, falls proposalImages leer ist
        selectedUploadId.value = 'original';
        setSuggestionImageUrl(defaultSuggestionImageUrl.value);
      }
    } catch (e) {
      console.error("Error parsing proposalImages:", e);
      proposalImages.value = [];
      selectedUploadId.value = 'original';
      setSuggestionImageUrl(defaultSuggestionImageUrl.value);
    }
  } else {
    proposalImages.value = [];
    selectedUploadId.value = 'original';
    setSuggestionImageUrl(defaultSuggestionImageUrl.value);
  }

  isLoadingProposalImages.value = false;

  // Wenn proposalImages leer ist, wird das Originalbild standardmäßig als hinzugefügt betrachtet,
  // aber wir speichern es erst in die DB, wenn der User explizit ein Bild hinzufügt.
  // Das verhindert das automatische Speichern beim reinen Aufrufen der Prüfstelle.

  if (route.name === 'Ueben' || route.name === 'Lernen' || route.name === 'Test' || route.name === 'EvaluationDetail') {
    // Wir laden trotzdem die Master-Definitionen (aus mm-checkpoint-settings)
    // Damit wir z.B. den "Zulässigen Messfehler" (permissibleMeasurementError) anzeigen können
    if (newMeasurementValues && Object.keys(newMeasurementValues).length > 0) {
      floatsToLocal(newMeasurementValues);
      masterDefinitions.value = newMeasurementValues;
      // Im Lernen-Modus übernehmen wir die Werte aus den Master-Definitionen direkt in die Anzeige
      if (route.name === 'Lernen') {
        Object.assign(measurement.value, newMeasurementValues);
      }
    }
    
    isLoadingProposalImages.value = false;
    return;
  }

  if (newMeasurementValues && Object.keys(newMeasurementValues).length > 0) {
    floatsToLocal(newMeasurementValues);
    console.log(newMeasurementValues);
    Object.keys(newMeasurementValues).forEach(key => {
      measurement.value[key] = newMeasurementValues[key];
    });
  } else {
    console.log("set default measurement values");
    setDefaultMeasurementValues();
  }
}

// Funktion zum Befüllen der Dropdowns
async function ensureActualCaseLoaded() {
  if (store.state.actualCase) return true;

  let caseId = route.query.case;
  const sessId = store.state.sessionId || route.query.session || route.params.sessionId;

  if (!caseId && sessId) {
    const sessionData = await APIServiceRestDB.getSession(sessId);
    if (sessionData) {
      store.commit('setSession', sessionData);
      store.commit('setSessionId', sessId);
      caseId = sessionData.object || sessionData.caseId || sessionData.objectSpecId || sessionData.toolbox || null;
      if (sessionData.course) {
        const courseData = await APIService.getCourse(sessionData.course, true);
        if (courseData) {
          store.dispatch('updateCourse', courseData);
        }
      }
    }
  }

  if (!caseId) return false;

  store.dispatch('updateCaseId', caseId);
  const caseTmp = await APIServiceRestDB.getObjectWithId(caseId);
  if (caseTmp) {
    store.dispatch('updateCase', caseTmp);
    return true;
  }

  const specTmp = await APIServiceRestDB.getToolbox(caseId);
  if (specTmp && !Array.isArray(specTmp)) {
    store.dispatch('updateCase', { _id: caseId, objectSpecId: caseId, title: specTmp.title || "Fallback Case" });
    return true;
  }

  return false;
}

async function fetchData() {
  const hasActualCase = await ensureActualCaseLoaded();
  if(!hasActualCase){
    console.warn("No actualCase in store, skipping fetchData");
    return;
  }

  // Refresh session data from server if we are in Test mode or have a sessionId
  // Wir machen das im EvaluationDetail-Modus und im Test-Modus IMMER, 
  // um sicherzugehen, dass wir die neuesten Resultate haben.
  if (route.name === 'EvaluationDetail' || route.name === 'Test' || store.state.sessionId || route.params.sessionId) {
    try {
      const sessId = store.state.sessionId || route.params.sessionId;
      if (sessId) {
        const freshSession = await APIServiceRestDB.getSession(sessId);
        if (freshSession) {
          store.commit('setSession', freshSession);
        }
      }
    } catch (e) {
      console.error("Error refreshing session during fetchData:", e);
    }
  }

  try {
    actualCase.value.parameters = store.state.actualCase;
    
    if (!actualCase.value.parameters.objectSpecId) {
       console.error("actualCase has no objectSpecId:", actualCase.value.parameters);
       return;
    }

    const specs = await APIServiceRestDB.getToolbox(actualCase.value.parameters.objectSpecId);
    if (!specs || Object.keys(specs).length === 0) {
        console.error("Could not fetch case specifications for objectSpecId:", actualCase.value.parameters.objectSpecId);
        return;
    }
    actualCase.value.specs =  convertJsonArraysToJs(specs);

    if (route.name === 'Inspection Points') {
      fetchExtraCoursesForObject(actualCase.value.parameters._id);
    }

    const ret  = await APIServiceRestDB.getWorkpiecesForCase(actualCase.value.specs._id);
    let allWorkpieces = convertJsonArraysToJs(ret);
    
    // Wenn eine Sitzung mit items aktiv ist, filtere die Werkstücke
    // AUSNAHME: Im Modus "Inspection Points" (Einrichten) werden immer alle Werkstücke angezeigt
    if (store.state.session && store.state.session.items && store.state.session.items.length > 0 && route.name !== 'Inspection Points') {
      const sessionItems = store.state.session.items;
      const filtered = allWorkpieces.filter(wp => 
        sessionItems.some(item => item.toolbox === wp._id || item._id === wp._id)
      );
      // Falls die Filterung Ergebnisse liefert, verwenden wir diese. 
      if (filtered.length > 0) {
        workpieces.value = filtered;
      } else {
        console.warn("Session items did not match any workpieces. Showing all.");
        workpieces.value = allWorkpieces;
      }
    } else {
      workpieces.value = allWorkpieces;
    }
  } catch (error) {
    console.error("Fehler beim Abrufen von Workpieces: ", error);
    workpieces.value = [];
  }
  
  if (workpieces.value && workpieces.value.length > 0) {
    let indexToSelect = 0;
    
    // Falls eine Sitzung aktiv ist, wählen wir das erste verfügbare Werkstück aus der gefilterten Liste
    // Andernfalls die bisherige Logik (z.B. Testmodus)
    if (!(store.state.session && store.state.session.items && store.state.session.items.length > 0)) {
      // Wenn im Testmodus, wähle das dritte Werkstück (Index 2), falls vorhanden
      if (route.name === 'Test' && workpieces.value.length >= 3) {
        indexToSelect = 2;
      }
    }
    
    selectedWorkpieceId.value = workpieces.value[indexToSelect]._id;

    await selectWorkpieceWithId(workpieces.value[indexToSelect]);
  } else {
    console.warn("No workpieces found for this case");
  }
}

watch(() => store.state.actualCase, async () => {
  fetchData();
});

watch(() => store.state.course, async (newCourse) => {
  if (newCourse && newCourse.uploads) {
    // Wenn der Kurs (neu) geladen wurde, stoßen wir fetchData erneut an,
    // um sicherzustellen, dass alle Bilder (auch aus proposalImages) korrekt 
    // mit den Kurs-Uploads gematcht und angezeigt werden können.
    fetchData();
  }
});

// Hier die Funktion hinzufügen
// eslint-disable-next-line no-unused-vars
/**
 * Handles a measurement change.
 *
 * @param {Object} newMeasurement - The new measurement to handle.
 * @param pointId
 * @return {Promise<void>} - A promise that resolves when the measurement change is handled.
 */
async function handleMeasurementChange(newMeasurement, pointId) {
  const targetPointId = pointId || selectedInspectionPoint.value._id;

  // Im Test-Modus werden die Resultate direkt im results-Feld der Session gespeichert
  if (route.name === 'Test') {
    if (isTestFinished.value) {
      return;
    }

    isSaving.value = true;
    lastSaved.value = null;
    
    // Wir holen uns den aktuellen Stand aus dem Store, um keine anderen Punkte zu überschreiben
    const currentResultsStr = store.state.session?.results;
    
    // Refresh session data again before saving to be 100% sure about testFinished
    if (store.state.sessionId) {
      try {
        const freshSession = await APIServiceRestDB.getSession(store.state.sessionId);
        if (freshSession) {
          store.commit('setSession', freshSession);
          if (freshSession.testFinished) {
            isSaving.value = false;
            return;
          }
        }
      } catch (e) {
        console.error("Error refreshing session during handleMeasurementChange:", e);
      }
    }

    let resultsObj = {};
    if (currentResultsStr) {
      try {
        resultsObj = JSON.parse(currentResultsStr);
      } catch (e) {
        console.error("Error parsing results for update:", e);
      }
    }

    const saveResult = {...newMeasurement};
    reformatFloats(saveResult);
    
    // Speichere den spezifischen Punkt im Results-Objekt
    resultsObj[targetPointId] = saveResult;

    try {
      const result = await APIServiceRestDB.updateSessionResults(store.state.sessionId, resultsObj);
      if (result) {
        // Session im Store aktualisieren, damit andere Views auch bescheid wissen
        const updatedSession = { ...store.state.session, results: JSON.stringify(resultsObj) };
        store.commit('setSession', updatedSession);
        lastSaved.value = new Date();
      } else {
        console.error("Speichern fehlgeschlagen (kein Resultat vom Server)");
      }
    } catch (e) {
      console.error("Error saving test result to session:", e);
      lastSaved.value = null;
    } finally {
      isSaving.value = false;
    }
    return;
  }

  // Nur im Einrichten-Modus (Inspection Points) dürfen die Master-Definitionen gespeichert oder geändert werden.
  if (route.name !== 'Inspection Points') return;

  const saveMeasurement = {...newMeasurement};
  reformatFloats(saveMeasurement);
  const currentDefs = await APIServiceRestDB.getCheckpointSettings(
    actualCase.value.parameters._id,
    targetPointId
  );
  const currentHasId = currentDefs && currentDefs._id;
  const dataToSave = {
    ...(currentHasId ? currentDefs : {}),
    ...saveMeasurement,
    _id: saveMeasurement._id || currentDefs?._id,
    toolboxId: actualCase.value.parameters._id,
    checkpointId: targetPointId
  };

  if (currentDefs?.proposalImages && !saveMeasurement.proposalImages) {
    dataToSave.proposalImages = currentDefs.proposalImages;
  }

  if(!dataToSave._id){
    const result = await APIServiceRestDB.saveCheckpointSettings(dataToSave);
    if (result?._id) {
      measurement.value._id = result._id;
    }
    await getMeasurementDefinitionsForCase();
  }else{
    const result = await APIServiceRestDB.updateCheckpointSettings(dataToSave);
    if (result?._id) {
      measurement.value._id = result._id;
    }
  }
}

let timeoutId = null;

// Methode zum Verarbeiten der Messwertänderungen mit Verzögerung
/**
 * Sets a delayed timeout for handling a measurement change.
 *
 * @param {any} newMeasurement - The new measurement value.
 * @return {void}
 */
function handleMeasurementChangeDelayed(newMeasurement) {
  clearTimeout(timeoutId); // Zuvor geplanten Timeout löschen
  
  // Wir kopieren das Measurement-Objekt und die aktuelle ID, 
  // um Race-Conditions beim Punkt-Wechsel zu vermeiden
  const measurementToSave = JSON.parse(JSON.stringify(newMeasurement));
  const pointIdToSave = selectedInspectionPoint.value._id;

  // Setzen Sie einen neuen Timeout für die Methode
  timeoutId = setTimeout(() => {
    handleMeasurementChange(measurementToSave, pointIdToSave);
  }, 1000); // Hier können Sie die Verzögerungszeit in Millisekunden anpassen (z.B. 500ms)
}

  // Watcher für `measurement` hinzufügen
  // watch(measurement.value, handleMeasurementChangeDelayed);

const isLoadingProposalImages = ref(false);
const isSaving = ref(false);
const lastSaved = ref(null);

onMounted(fetchData);

// Funktion, die beim Ändern des ersten Dropdowns aufgerufen wird
/**
 * Handles the change event of the workpiece dropdown.
 *
 * @param {Event} event - The change event object.
 * @return {void}
 */
async function handleWorkpieceDropdownChange(event) {
  const selectedValue =  workpieces.value.find(item => item._id === event.target.value);
  await selectWorkpieceWithId(selectedValue);
}

async function selectWorkpieceWithId(selectedValue){
  if(selectedValue){
    selectedWorkpiece.value = selectedValue;
    selectedWorkpieceId.value = selectedValue._id;
    await updateDrawingImageUrl();

    inspectionPoints.value = await APIServiceRestDB.getCheckpoints(selectedValue._id);

    if (inspectionPoints.value.length > 0) {
      // Wenn eine Sitzung aktiv ist, wählen wir den ersten gefilterten Punkt
      const initialPoint = filteredInspectionPoints.value.length > 0 
        ? filteredInspectionPoints.value[0]._id 
        : inspectionPoints.value[0]._id;
        
      setInspectionPoint(initialPoint);

      // Set select element
      setTimeout(() => {
        if (inspectionPointSelect.value) {
          inspectionPointSelect.value.value = initialPoint;
        }
      }, 200);
    }

  }else{
    selectedWorkpiece.value = {};
  }
}

async function handleInspectionPointDropdownChange(event){
  const selectedValue = event.target.value;
  setInspectionPoint(selectedValue);
}

function selectFromSearchResults(item) {
  setInspectionPoint(item._id);
  if (inspectionPointSelect.value) {
    inspectionPointSelect.value.value = item._id;
  }
  showSearchResults.value = false;
  searchQuery.value = item.title; // Setze den Namen in das Suchfeld
  
  // Wir müssen das Dropdown reaktiv aktualisieren, da es nun auf filteredInspectionPoints (alle) basiert
  const foundIndex = inspectionPoints.value.findIndex(p => p._id === item._id);
  if (foundIndex !== -1 && inspectionPointSelect.value) {
    inspectionPointSelect.value.selectedIndex = foundIndex;
  }
}

function clearSearch() {
  searchQuery.value = "";
  showSearchResults.value = false;
}

function handleBlur() {
  setTimeout(() => {
    showSearchResults.value = false;
  }, 200);
}
// Funktion zum Auswählen des vorherigen InspectionPoints
function selectPreviousInspectionPoint() {
  const currentIndex = filteredInspectionPoints.value.findIndex(item => item._id === selectedInspectionPoint.value._id);
  if (currentIndex > 0) {  // Es gibt ein vorheriges Element
    const prevItem = filteredInspectionPoints.value[currentIndex - 1];
    setInspectionPoint(prevItem._id);
    inspectionPointSelect.value.value = prevItem._id;  // Aktualisiere das Select-Element
  }
}

// Funktion zum Auswählen des nächsten InspectionPoints
function selectNextInspectionPoint() {
  const currentIndex = filteredInspectionPoints.value.findIndex(item => item._id === selectedInspectionPoint.value._id);
  if (currentIndex < filteredInspectionPoints.value.length - 1) { // Es gibt ein nächstes Element
    const nextItem = filteredInspectionPoints.value[currentIndex + 1];
    setInspectionPoint(nextItem._id);
    inspectionPointSelect.value.value = nextItem._id;  // Aktualisiere das Select-Element
  }
}

function handleInspectionPointClick(inspectionPoint) {
  setInspectionPoint(inspectionPoint._id);
  inspectionPointSelect.value.value = inspectionPoint._id;
}

/**
 * Sets the selected inspection point based on the provided id.
 * Updates the suggestion image URL, marker position and visibility.
 * Also sets the title value and retrieves measurement definitions for the case.
 *
 * @param {string} id - The id of the inspection point to set.
 *
 * @return {void}
 */
async function setInspectionPoint(id){
  const foundItem = inspectionPoints.value.find(item => item._id === id);

  if(foundItem) {
    selectedInspectionPoint.value = foundItem; // Speichern des gefundenen Objekts

    if (route.name === 'Inspection Points' && canEditMarkers.value) {
      const arr = getMarkerForMode(foundItem) || [5, 5, 10, 10];
      editableMarker.value = [...arr];
    }

    // Resolve Suggestion Image URL (C-Learning Uploads or fallback)
    let imageUrl = '';
    if (selectedInspectionPoint.value.image) {
      imageUrl = getAssetUrl(
        selectedInspectionPoint.value.image, 
        actualCase.value.specs.number || actualCase.value.parameters.number || '0', 
        'wp',
        selectedWorkpiece.value.id || selectedWorkpiece.value.number || '0',
        'png'
      );
      const upload = findUploadByName(selectedInspectionPoint.value.image, 'png');
      if (upload && upload.file) {
        const tokenUrl = await getFileTokenUrl(APIService, upload.file.id);
        if (tokenUrl) imageUrl = tokenUrl;
      }
    }
    setSuggestionImageUrl(imageUrl);
    defaultSuggestionImageUrl.value = imageUrl; // Store default
    
    // selectedUploadId.value = null; // Entfernt, da es bereits in getMeasurementDefinitionsForCase gesetzt wird

    const element = document.querySelector('.marker-rectangle');
    const activeMarker = getMarkerForMode(selectedInspectionPoint.value);
    if (element && activeMarker) {
      const coords = activeMarker;
      element.style.left = coords[0]+"%";
      element.style.top = coords[1]+"%";
      element.style.width = coords[2]+"%";
      element.style.height = coords[3]+"%";
      element.style.display = "block";
    } else if (element) {
      element.style.display = "none";
    }
  } else {
    selectedInspectionPoint.value = {}; // Zurücksetzen, falls kein Objekt gefunden wurde
  }
  title.value = selectedInspectionPoint.value.title;
  //setDefaultMeasurementValues();
  await getMeasurementDefinitionsForCase();
}

function getThumbUrlEffective(upload) {
  if (uploadTokenUrls.value[upload.file.id]) return uploadTokenUrls.value[upload.file.id];
  if (upload.path && /^https?:\/\//i.test(upload.path)) return upload.path;
  if (upload.path && !isContentPath(upload.path)) {
    return getAssetUrl(
      upload.path,
      actualCase.value.specs.number || actualCase.value.parameters.number || '0',
      'wp',
      selectedWorkpiece.value.id || selectedWorkpiece.value.number || '0',
      'png'
    );
  }
  return '';
}

const selectedUploadId = ref(null);

function isSelectedUpload(upload) {
  if (upload === 'original') {
    return selectedUploadId.value === 'original' || selectedUploadId.value === null;
  }
  return selectedUploadId.value === upload.file.id;
}

async function selectUploadAsSuggestion(upload) {
  if (upload === 'original') {
    selectedUploadId.value = 'original';
    setSuggestionImageUrl(defaultSuggestionImageUrl.value);
    return;
  }
  selectedUploadId.value = upload.file.id;
  
  // Versuche erst über den Pfad zu laden
  if (upload.path && isContentPath(upload.path)) {
    const token = store.state.user?.tokens?.access?.token;
    const blobUrl = await getAuthenticatedFileUrl(upload.path, token);
    if (blobUrl) {
      setSuggestionImageUrl(blobUrl);
      return;
    }
  }
  
  // Fallback
  const tokenUrl = await getFileTokenUrl(APIService, upload.file.id);
  if (tokenUrl) {
    setSuggestionImageUrl(tokenUrl);
  }
}

function isImageAdded(fileId) {
  const currentProposals = proposalImages.value || [];
  
  if (currentProposals.length === 0) {
    // Falls die Liste in der DB noch leer ist, zeigen wir nur das Originalbild an
    return String(fileId) === 'original';
  }

  return currentProposals.some(x => {
    // Sicherstellen, dass wir IDs robust vergleichen (Strings)
    return String(x.fileId) === String(fileId);
  });
}

const currentSelectedUploadId = computed(() => {
  if (selectedUploadId.value === 'original' || selectedUploadId.value === null) {
    return 'original';
  }
  return selectedUploadId.value;
});

async function addProposalImage(uploadOrId) {
  let newImage;
  let changed = false;
  
  // Wenn die Liste noch leer ist, füge zuerst das Originalbild hinzu, 
  // falls ein anderes Bild hinzugefügt wird
  if (proposalImages.value.length === 0 && uploadOrId !== 'original') {
    const originalImage = {
      fileId: 'original',
      originalName: selectedInspectionPoint.value.image,
      path: selectedInspectionPoint.value.image,
      description: 'Original Image'
    };
    proposalImages.value.push(originalImage);
    changed = true;
  }

  if (uploadOrId === 'original') {
    newImage = {
      fileId: 'original',
      originalName: selectedInspectionPoint.value.image,
      path: selectedInspectionPoint.value.image,
      description: 'Original Image'
    };
  } else {
    const uploadId = typeof uploadOrId === 'string' ? uploadOrId : uploadOrId.file.id;
    const upload = imageUploads.value.find(u => u.file.id === uploadId);
      
    if (!upload) return;
    
    newImage = {
      fileId: upload.file.id,
      originalName: upload.file.originalName,
      path: upload.path,
      description: upload.description
    };
  }
  
  if (!proposalImages.value.some(x => x.fileId === newImage.fileId)) {
    proposalImages.value.push(newImage);
    changed = true;
  }

  if (changed) {
    await saveProposalImages();
  }
}

async function removeProposalImage(fileId) {
  // Verhindere das Entfernen des Originalbildes, wenn es das einzige Element ist.
  // Das stellt sicher, dass immer mindestens ein Bild (das Standardbild) vorhanden ist.
  if (fileId === 'original' && (proposalImages.value.length === 1 || proposalImages.value.length === 0)) {
    return;
  }
  proposalImages.value = proposalImages.value.filter(x => x.fileId !== fileId);
  await saveProposalImages();
}

async function saveProposalImages() {
  if (route.name !== 'Inspection Points') return;
  const imagesToSave = (proposalImages.value || []).filter(image => image && image.fileId);
  if (imagesToSave.length === 0) {
    console.warn("Skipping empty proposalImages save.");
    return;
  }
  console.log("Saving proposal images:", imagesToSave);
  
  const currentDefs = await APIServiceRestDB.getCheckpointSettings(
    actualCase.value.parameters._id, 
    selectedInspectionPoint.value._id
  );
  
  const dataToSave = {
    ...currentDefs,
    toolboxId: actualCase.value.parameters._id,
    checkpointId: selectedInspectionPoint.value._id,
    proposalImages: JSON.stringify(imagesToSave)
  };
  
  if (dataToSave._id) {
    const result = await APIServiceRestDB.updateCheckpointSettings(dataToSave);
    if (result) {
      measurement.value._id = result._id;
    }
  } else {
    const result = await APIServiceRestDB.saveCheckpointSettings(dataToSave);
    if (result && result._id) {
      measurement.value._id = result._id;
      // Definitions für den gesamten Case neu laden, falls nötig
      await getMeasurementDefinitionsForCase();
    }
  }
}


const uploadTokenUrls = ref({});

watch(imageUploads, async (newUploads) => {
  console.log("Image uploads changed:", newUploads);
  for (const upload of newUploads) {
    if (!uploadTokenUrls.value[upload.file.id]) {
      // Wenn der Pfad direkt vorhanden ist (aus proposalImages), nutzen wir diesen bevorzugt
      if (upload.path && /^https?:\/\//i.test(upload.path)) {
        uploadTokenUrls.value[upload.file.id] = upload.path;
        continue;
      }
      if (upload.path && isContentPath(upload.path)) {
        const token = store.state.user?.tokens?.access?.token;
        const blobUrl = await getAuthenticatedFileUrl(upload.path, token);
        if (blobUrl) {
          uploadTokenUrls.value[upload.file.id] = blobUrl;
          continue;
        }
      }
      // Fallback auf die Standard-Methode über die Date-ID
      uploadTokenUrls.value[upload.file.id] = await getFileTokenUrl(APIService, upload.file.id);
    }
  }

  if (
    route.name === 'Inspection Points'
    && newUploads.length > 0
    && (!suggestionImageUrl.value || suggestionImageFailed.value)
    && (selectedUploadId.value === null || selectedUploadId.value === 'original')
  ) {
    await selectUploadAsSuggestion(newUploads[0]);
  }
}, { immediate: true, deep: true });


// eslint-disable-next-line no-unused-vars
function resolveDrawingPdfUrl(drawingFileName) {
  const resolvedUrl = getAssetUrl(
    drawingFileName,
    actualCase.value.specs.number || actualCase.value.parameters.number || '0',
    'wp',
    selectedWorkpiece.value.id || selectedWorkpiece.value.number || '0',
    'pdf'
  );
  console.log('[PDF DEBUG] resolveDrawingPdfUrl', {
    drawingFileName,
    toolboxNumber: actualCase.value.specs.number || actualCase.value.parameters.number || '0',
    workpieceIdentifier: selectedWorkpiece.value.id || selectedWorkpiece.value.number || '0',
    selectedWorkpieceId: selectedWorkpiece.value?._id,
    selectedWorkpieceNumber: selectedWorkpiece.value?.number,
    selectedWorkpieceDrawing: selectedWorkpiece.value?.drawing,
    showIsoGpsPreview: showIsoGpsPreview.value,
    resolvedUrl
  });
  return resolvedUrl;
}

function openResolvedPdf(drawingFileName) {
  if (!drawingFileName) return;
  console.log('[PDF DEBUG] openResolvedPdf start', {
    drawingFileName,
    mode: showIsoGpsPreview.value ? 'ISO-GPS' : 'NORMAL'
  });
  const url = resolveDrawingPdfUrl(drawingFileName);
  if (!url) return;
  console.log('[PDF DEBUG] openResolvedPdf final URL', { url });

  const popup = window.open('', '_blank');
  if (!popup) {
    window.open(url, '_blank');
    return;
  }

  // Robust: PDF-Inhalt erst laden und als Blob im neuen Tab anzeigen.
  // Dadurch umgehen wir Browser-/Server-Navigationseffekte, die vereinzelt auf Login umleiten.
  fetch(url, { method: 'GET', redirect: 'follow', cache: 'no-store' })
    .then(async (response) => {
      console.log('[PDF DEBUG] preflight', {
        requestUrl: url,
        finalUrl: response.url,
        status: response.status,
        ok: response.ok,
        redirected: response.redirected,
        contentType: response.headers.get('content-type')
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      popup.location.replace(blobUrl);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    })
    .catch((error) => {
      console.warn('[PDF DEBUG] blob-open failed, fallback direct URL', { url, error: String(error) });
      popup.location.replace(url);
    });
}

async function openDrawing() {
  if(selectedWorkpiece.value && selectedWorkpiece.value.drawing) {
    const drawingFileName = resolveIsoGpsVariantFilename(selectedWorkpiece.value.drawing, 'pdf', false);
    console.log('[PDF DEBUG] openDrawing', {
      originalDrawing: selectedWorkpiece.value.drawing,
      drawingFileName
    });
    openResolvedPdf(drawingFileName);
  }
}

// eslint-disable-next-line no-unused-vars
async function openIsoGpsDrawing() {
  if (selectedWorkpiece.value && selectedWorkpiece.value.drawing) {
    const isoGpsFileName = resolveIsoGpsVariantFilename(selectedWorkpiece.value.drawing, 'pdf', true);
    console.log('[PDF DEBUG] openIsoGpsDrawing', {
      originalDrawing: selectedWorkpiece.value.drawing,
      isoGpsFileName
    });
    openResolvedPdf(isoGpsFileName);
  }
}

async function openCurrentDrawing() {
  if (showIsoGpsPreview.value) {
    await openIsoGpsDrawing();
    return;
  }
  await openDrawing();
}

function validateFloat(value) {
  if (value === null) {
    return false;
  }
  if (typeof value !== 'string') {
    value = String(value);
  }
  return /^-?\d*([.,]\d*)?$/.test(value);
}

// Methode zum Verarbeiten von Eingaben
function handleInput(fieldName, event) {
  let inputValue = event.target.value;
  // Überprüfen, ob die Eingabe eine gültige Fließkommazahl ist
  if (!validateFloat(inputValue)) {
    // Die Eingabe ist keine gültige Fließkommazahl, setzen Sie den Wert im measurement-Objekt auf den vorherigen Wert
    measurement.value[fieldName] = measurement.value[fieldName+"_old"]; // Setzen Sie den Wert auf den vorherigen Wert zurück
  }else{
    measurement.value[fieldName+"_old"] = measurement.value[fieldName];
    // Speichern der Änderung im Einrichten-Modus, Test-Modus oder Üben-Modus
    if (route.name === 'Inspection Points' || route.name === 'Test' || route.name === 'Ueben') {
      handleMeasurementChangeDelayed(measurement.value);
    }
  }
}

function handleFocus(fieldName) {
  if (String(measurement.value[fieldName]) === "0") {
    measurement.value[fieldName] = "";
  }
}

function handleBlurField(fieldName) {
  if (route.name === 'Inspection Points' || route.name === 'Test') {
    if (measurement.value[fieldName] === "" || measurement.value[fieldName] === null) {
      measurement.value[fieldName] = "0";
    }
  }
}

const masterDefinitions = ref({});

async function checkUeben() {
  masterDefinitions.value = await APIServiceRestDB.getCheckpointSettings(
    actualCase.value.parameters._id, 
    selectedInspectionPoint.value._id
  );
  floatsToLocal(masterDefinitions.value);
  isUebenChecked.value = true;
}

function resetUeben() {
  isUebenChecked.value = false;
  setDefaultMeasurementValues();
}

function getCompareColor(fieldName) {
  if (!isUebenChecked.value && route.name !== 'EvaluationDetail') return '';
  
  if (measurement.value[fieldName] === undefined || measurement.value[fieldName] === null || measurement.value[fieldName] === '') return '';
  
  const userValRaw = measurement.value[fieldName];
  const userValue = String(userValRaw).replace(',', '.');
  
  // Als Vergleichswert (Master) nutzen wir primär die masterDefinitions, 
  // falls diese leer sind, nehmen wir die Vorgabe (selectedInspectionPoint).
  // WICHTIG: 0 ist ein gültiger Wert, den der Ausbilder eingeben kann!
  const hasMasterVal = masterDefinitions.value && 
                       masterDefinitions.value[fieldName] !== undefined && 
                       masterDefinitions.value[fieldName] !== null && 
                       masterDefinitions.value[fieldName] !== '' &&
                       Object.keys(masterDefinitions.value).length > 0;
                       
  const masterValRaw = hasMasterVal
    ? masterDefinitions.value[fieldName] 
    : (selectedInspectionPoint.value ? selectedInspectionPoint.value[fieldName] : '');
    
  const masterValue = String(masterValRaw !== undefined && masterValRaw !== null ? masterValRaw : '').replace(',', '.');
  
  if (isFloatLocal(userValue) && isFloatLocal(masterValue)) {
    if (parseFloat(userValue) === parseFloat(masterValue)) {
      return 'green';
    }
  } else if (userValue === masterValue) {
    return 'green';
  }
  
  return 'red';
}

function getDeviation(fieldName) {
  if (route.name !== 'EvaluationDetail' && !(route.name === 'Ueben' && isUebenChecked.value)) return '';
  // Wir prüfen nur, ob überhaupt ein Wert eingegeben wurde
  if (measurement.value[fieldName] === undefined || measurement.value[fieldName] === null || measurement.value[fieldName] === '') return '';
  
  // Als Vergleichswert (Master) nutzen wir primär die masterDefinitions, 
  // falls diese leer sind, nehmen wir die Vorgabe (selectedInspectionPoint).
  // WICHTIG: 0 ist ein gültiger Wert, den der Ausbilder eingeben kann!
  const hasMasterVal = masterDefinitions.value && 
                       masterDefinitions.value[fieldName] !== undefined && 
                       masterDefinitions.value[fieldName] !== null && 
                       masterDefinitions.value[fieldName] !== '' &&
                       Object.keys(masterDefinitions.value).length > 0;

  const masterValRaw = hasMasterVal
    ? masterDefinitions.value[fieldName] 
    : (selectedInspectionPoint.value ? selectedInspectionPoint.value[fieldName] : '');
    
  const mVal = String(masterValRaw).replace(',', '.');
  const uVal = String(measurement.value[fieldName]).replace(',', '.');
  
  if (!isFloatLocal(uVal) || !isFloatLocal(mVal)) return '';

  const userValue = parseFloat(uVal);
  const masterValue = parseFloat(mVal);
  
  // Wenn masterValue 0 ist, aber nominalDimension eigentlich 47 sein sollte, 
  // dann stimmt was mit der Datenladung der masterDefinitions nicht. 
  // Durch den Fallback oben auf selectedInspectionPoint sollte das nun abgefangen sein.
  
  const diff = userValue - masterValue;
  if (isNaN(diff)) return '';

  return (diff > 0 ? '+' : '') + diff.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
}

function isFloatLocal(val) {
    const floatRegex = /^-?\d+(\.\d+)?$/;
    if (!val || typeof val !== 'string') return false;
    if (!floatRegex.test(val.replace(',', '.')))
        return false;

    return !isNaN(parseFloat(val));
}

function getMarkerStyle(ip) {
  const isActiveEditable = route.name === 'Inspection Points' &&
    canEditMarkers.value &&
    selectedInspectionPoint.value &&
    selectedInspectionPoint.value._id === ip._id;

  if (isActiveEditable) {
    return {
      left: editableMarker.value[0] + '%',
      top: editableMarker.value[1] + '%',
      width: editableMarker.value[2] + '%',
      height: editableMarker.value[3] + '%',
      display: 'block'
    };
  }

  const marker = getMarkerForMode(ip);
  if (!marker) return { display: 'none' };

  return {
    left: marker[0] + '%',
    top: marker[1] + '%',
    width: marker[2] + '%',
    height: marker[3] + '%',
    display: 'block'
  };
}

function onInspectionPointClick(inspectionPoint) {
  if (hasDragged.value) {
    hasDragged.value = false;
    return;
  }
  handleInspectionPointClick(inspectionPoint);
}

function onMarkerMouseDown(e) {
  if (!canEditMarkers.value) return;
  e.preventDefault();
  hasDragged.value = false;
  isDragging.value = true;
  dragStartData.value = {
    mouseX: e.clientX,
    mouseY: e.clientY,
    markerX: editableMarker.value[0],
    markerY: editableMarker.value[1],
    markerW: editableMarker.value[2],
    markerH: editableMarker.value[3]
  };
  window.addEventListener('mousemove', onMarkerMouseMove);
  window.addEventListener('mouseup', onMarkerMouseUp);
}

function onResizeMouseDown(e) {
  if (!canEditMarkers.value) return;
  e.preventDefault();
  hasDragged.value = false;
  isResizing.value = true;
  dragStartData.value = {
    mouseX: e.clientX,
    mouseY: e.clientY,
    markerX: editableMarker.value[0],
    markerY: editableMarker.value[1],
    markerW: editableMarker.value[2],
    markerH: editableMarker.value[3]
  };
  window.addEventListener('mousemove', onMarkerMouseMove);
  window.addEventListener('mouseup', onMarkerMouseUp);
}

function onMarkerMouseMove(e) {
  const container = markerContainerRef.value;
  if (!container) return;

  hasDragged.value = true;

  const rect = container.getBoundingClientRect();
  const dxPct = ((e.clientX - dragStartData.value.mouseX) / rect.width) * 100;
  const dyPct = ((e.clientY - dragStartData.value.mouseY) / rect.height) * 100;

  if (isDragging.value) {
    const w = dragStartData.value.markerW;
    const h = dragStartData.value.markerH;
    const newX = Math.max(0, Math.min(100 - w, dragStartData.value.markerX + dxPct));
    const newY = Math.max(0, Math.min(100 - h, dragStartData.value.markerY + dyPct));
    editableMarker.value = [
      parseFloat(newX.toFixed(2)),
      parseFloat(newY.toFixed(2)),
      w,
      h
    ];
  } else if (isResizing.value) {
    const newW = Math.max(2, Math.min(100 - dragStartData.value.markerX, dragStartData.value.markerW + dxPct));
    const newH = Math.max(2, Math.min(100 - dragStartData.value.markerY, dragStartData.value.markerH + dyPct));
    editableMarker.value = [
      dragStartData.value.markerX,
      dragStartData.value.markerY,
      parseFloat(newW.toFixed(2)),
      parseFloat(newH.toFixed(2))
    ];
  }
}

function onMarkerMouseUp() {
  isDragging.value = false;
  isResizing.value = false;
  window.removeEventListener('mousemove', onMarkerMouseMove);
  window.removeEventListener('mouseup', onMarkerMouseUp);
}

async function saveMarker() {
  if (!canEditMarkers.value) return;
  if (!isAdmin.value) return;
  if (!selectedInspectionPoint.value || !selectedInspectionPoint.value._id) return;
  isSavingMarker.value = true;
  try {
    const markerToSave = [...editableMarker.value];
    const markerField = activeMarkerField.value;
    const result = await APIServiceRestDB.updateCheckpointMarker(
      selectedInspectionPoint.value._id,
      markerToSave,
      markerField
    );
    if (result) {
      const idx = inspectionPoints.value.findIndex(ip => ip._id === selectedInspectionPoint.value._id);
      if (idx !== -1) {
        inspectionPoints.value[idx][markerField] = markerToSave;
      }
      selectedInspectionPoint.value[markerField] = markerToSave;
    }
  } catch (e) {
    console.error("Error saving marker:", e);
  } finally {
    isSavingMarker.value = false;
  }
}

watch(showIsoGpsPreview, () => {
  if (route.name !== 'Inspection Points' || !canEditMarkers.value) return;
  const arr = getMarkerForMode(selectedInspectionPoint.value) || [5, 5, 10, 10];
  editableMarker.value = [...arr];
});

</script>

<style scoped>
.invisible-rectangle {
  position: absolute;
  cursor: pointer;
  box-sizing: border-box;
}

.editable-marker {
  border: 2px solid red !important;
  cursor: move;
  background-color: rgba(255, 0, 0, 0.08);
  display: block !important;
  pointer-events: auto;
  user-select: none;
}

.marker-resize-handle {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 10px;
  height: 10px;
  background-color: red;
  border-radius: 2px;
  cursor: se-resize;
}

.suggestion-empty-state {
  height: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #777;
  background: #fafafa;
  border: 1px dashed #d0d0d0;
}

.thumb-missing {
  width: 100%;
  height: 100%;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border: 1px dashed #d0d0d0;
}

.readonly-finished-banner {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  background: #b00020;
  color: #fff;
  border-bottom: 3px solid #7a0016;
  font-size: 0.98rem;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}
</style>
